import { useEffect, useState } from 'react';

/**
 * Approving a terminal.
 *
 * The half of `reef login` a person sees. The CLI never handles a credential —
 * it holds a device code nobody can do anything with until somebody signed in
 * here says yes — so this screen is the entire security boundary of the flow,
 * and it is built to be read rather than clicked through.
 *
 * Which is why the code is shown back rather than merely accepted. The attack
 * this shape has is somebody being talked into approving a code that is not
 * theirs — read to them over a phone call, pasted into a chat — so the screen
 * says plainly what approving does and which terminal it belongs to, and asks
 * for a deliberate press. A page that auto-approved from the URL would turn a
 * link into a session.
 */
export function DeviceView() {
  const [code, setCode] = useState('');
  const [state, setState] = useState<'idle' | 'working' | 'approved' | 'denied'>('idle');
  const [error, setError] = useState<string | null>(null);

  /*
   * Prefilled from the query, because the CLI prints a URL that carries the
   * code and typing eight characters off a terminal is a needless chance to get
   * it wrong. Prefilled and *not* submitted: the press is the approval.
   */
  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('user_code');
    if (fromUrl) setCode(fromUrl.toUpperCase());
  }, []);

  const decide = async (approve: boolean) => {
    setState('working');
    setError(null);
    const userCode = code.trim().toUpperCase();

    try {
      /*
       * Claim the code first, and this step is not optional.
       *
       * A device code is written with no `userId` — nobody has said it is
       * theirs yet — and `\/device\/approve` refuses a row in that state with
       * `invalid_request` (`DEVICE_CODE_NOT_CLAIMED`). `GET /device` is what
       * binds the pending code to the session making the request, so it has to
       * run before the approval and with the same credentials.
       *
       * Approving without it is the mistake this screen shipped with: the whole
       * flow worked end to end except the one press it exists for, and the
       * error named the request rather than the missing step.
       */
      const claim = await fetch(`/api/auth/device?user_code=${encodeURIComponent(userCode)}`, {
        credentials: 'include',
        headers: { accept: 'application/json' },
      });

      if (!claim.ok) {
        const body = (await claim.json().catch(() => ({}))) as { error_description?: string; error?: string };
        // A code that is wrong and a code that has expired are different things
        // to a reader, and the server distinguishes them — so pass its own
        // wording through rather than flattening both to "invalid".
        throw new Error(
          body.error_description ?? body.error ?? `That code was not recognised (${claim.status}).`,
        );
      }

      const response = await fetch(`/api/auth/device/${approve ? 'approve' : 'deny'}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ userCode }),
      });

      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as {
          error_description?: string;
          message?: string;
          error?: string;
        };
        throw new Error(
          body.error_description ??
            body.message ??
            body.error ??
            `The code was not accepted (${response.status}).`,
        );
      }
      setState(approve ? 'approved' : 'denied');
    } catch (cause) {
      setState('idle');
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  if (state === 'approved' || state === 'denied') {
    return (
      <main className="main">
        <h1>{state === 'approved' ? 'Terminal approved' : 'Request denied'}</h1>
        <p className="hint">
          {state === 'approved'
            ? 'The terminal you started this from is signed in as you. You can close this tab.'
            : 'Nothing was signed in. If you did not start this, no further action is needed — the code expires on its own.'}
        </p>
      </main>
    );
  }

  return (
    <main className="main">
      <h1>Approve a terminal</h1>
      <p className="hint" style={{ marginTop: 0 }}>
        A command line asked to sign in as you. Check the code below matches the one it printed.
      </p>

      {/*
        The consequence, before the button rather than after it. Approving hands
        a terminal a session with everything this account can reach, and the one
        thing a reader needs to know is that they should not do it for a code
        somebody else read out to them.
      */}
      <div className="banner warn">
        <p style={{ marginTop: 0 }}>
          Approving signs that terminal in as you, with the same access you have here.
        </p>
        <p style={{ marginBottom: 0 }}>
          Only approve a code you are looking at yourself. If somebody sent you this link or read you
          a code, deny it.
        </p>
      </div>

      <div className="card">
        <div className="field">
          <label htmlFor="device-user-code">Code from the terminal</label>
          <input
            id="device-user-code"
            value={code}
            autoComplete="off"
            spellCheck={false}
            placeholder="ABCD-EFGH"
            onChange={(event) => setCode(event.target.value.toUpperCase())}
          />
        </div>

        {error && <div className="banner danger">{error}</div>}

        <div className="row" style={{ marginTop: '0.75rem' }}>
          <button
            className="primary"
            disabled={code.trim().length === 0 || state === 'working'}
            onClick={() => void decide(true)}
          >
            {state === 'working' ? 'Working…' : 'Approve this terminal'}
          </button>
          <button
            className="ghost"
            disabled={code.trim().length === 0 || state === 'working'}
            onClick={() => void decide(false)}
          >
            Deny
          </button>
        </div>
      </div>
    </main>
  );
}
